<template>
  <v-card outlined elevation="0" class="garage-recommendation rounded-lg">
    <!-- Header (standard typography) -->
    <div class="d-flex align-center px-3 py-2">
      <v-icon color="primary" small class="mr-2">mdi-creation</v-icon>
      <span class="text-subtitle-2 font-weight-bold primary--text"
        >Garage Bid Recommendation</span
      >
      <v-spacer />
      <v-btn
        v-if="allowRegenerate && !loading && !generating"
        outlined
        small
        color="primary"
        :loading="regenerating"
        @click="$emit('regenerate')"
      >
        <v-icon left small>mdi-refresh</v-icon>Regenerate
      </v-btn>
    </div>
    <v-divider />

    <div class="pa-4">
      <!-- Loading -->
      <v-skeleton-loader v-if="loading" type="article, list-item-three-line" />

      <!-- Generating (recommendation is being computed server-side) -->
      <div
        v-else-if="generating"
        class="d-flex align-center py-4 text-body-2 grey--text text--darken-1"
      >
        <v-progress-circular
          indeterminate
          size="22"
          width="2"
          color="primary"
          class="mr-3"
        />
        Generating recommendation… it will appear here shortly.
      </div>

      <!-- Full recommendation -->
      <template v-else-if="garages.length">
        <div class="text-body-2 grey--text text--darken-1 mb-3">
          Recommended garage based on previous work history
        </div>

        <!-- Metadata strip -->
        <v-card outlined elevation="0" class="rounded-lg pa-4 mb-4 meta-strip">
          <div class="meta-grid">
            <div class="meta-item">
              <v-icon color="primary" class="mr-2"
                >mdi-card-account-details-outline</v-icon
              >
              <div>
                <div class="meta-label">Booking ID</div>
                <div class="meta-value">{{ recommendation.bookingId }}</div>
              </div>
            </div>
            <div class="meta-item">
              <v-icon color="primary" class="mr-2">mdi-car-side</v-icon>
              <div>
                <div class="meta-label">Vehicle</div>
                <div class="meta-value">{{ vehicleLabel || 'N/A' }}</div>
              </div>
            </div>
            <div class="meta-item">
              <v-icon color="primary" class="mr-2">mdi-factory</v-icon>
              <div>
                <div class="meta-label">Make</div>
                <div class="meta-value">{{ recommendation.make || 'N/A' }}</div>
              </div>
            </div>
            <div class="meta-item">
              <v-icon color="primary" class="mr-2">mdi-car-outline</v-icon>
              <div>
                <div class="meta-label">Model</div>
                <div class="meta-value">
                  {{ recommendation.model || 'N/A' }}
                </div>
              </div>
            </div>
            <div class="meta-item">
              <v-icon color="primary" class="mr-2">mdi-calendar</v-icon>
              <div>
                <div class="meta-label">Year of Manufacture</div>
                <div class="meta-value">
                  {{ recommendation.yearOfManufacture || 'N/A' }}
                </div>
              </div>
            </div>
            <div class="meta-item">
              <v-icon color="primary" class="mr-2"
                >mdi-account-group-outline</v-icon
              >
              <div>
                <div class="meta-label">Candidate Pool</div>
                <div class="meta-value">
                  {{ recommendation.candidatePool || 'N/A' }}
                </div>
              </div>
            </div>
            <div class="meta-item">
              <v-icon color="primary" class="mr-2">mdi-clock-outline</v-icon>
              <div>
                <div class="meta-label">Generated At</div>
                <div class="meta-value">
                  {{ recommendation.generatedAt | formatToHuman }}
                </div>
              </div>
            </div>
          </div>
        </v-card>

        <!-- Recommended banner -->
        <v-card
          v-if="topPick"
          elevation="0"
          class="rounded-lg pa-5 mb-4 recommend-banner"
        >
          <div class="d-flex align-center flex-wrap">
            <v-avatar size="64" class="banner-medal mr-4">
              <v-icon size="34" color="white">mdi-creation</v-icon>
            </v-avatar>
            <div class="tw-flex-1 tw-min-w-0">
              <div class="d-flex align-center flex-wrap">
                <span
                  class="text-subtitle-1 font-weight-bold banner-title mr-3"
                >
                  Recommended Garage: {{ topPick.garageName }}
                </span>
                <v-spacer />
                <v-chip color="secondary" class="font-weight-bold white--text">
                  <v-icon left small>mdi-creation</v-icon>Top Recommendation
                </v-chip>
              </div>
              <div class="banner-stats mt-3">
                <div class="bstat">
                  <v-icon small color="secondary" class="mr-1"
                    >mdi-map-marker</v-icon
                  >
                  <span class="bstat-label">Region</span>
                  <span class="bstat-value">{{ topPick.region || 'N/A' }}</span>
                </div>
                <div class="bstat">
                  <v-icon small color="secondary" class="mr-1"
                    >mdi-star-outline</v-icon
                  >
                  <span class="bstat-label">Score</span>
                  <span class="bstat-value">{{ topPick.score }}</span>
                </div>
                <div class="bstat">
                  <v-icon small color="secondary" class="mr-1"
                    >mdi-shield-star-outline</v-icon
                  >
                  <span class="bstat-label">Specialist</span>
                  <span class="bstat-value">{{
                    topPick.rareAndSpecialized ? 'Yes' : 'No'
                  }}</span>
                </div>
                <div class="bstat">
                  <v-icon small color="secondary" class="mr-1"
                    >mdi-calendar-check</v-icon
                  >
                  <span class="bstat-label">Last Repaired</span>
                  <span class="bstat-value">{{
                    topPick.lastRepairedAt | formatToHuman
                  }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="d-flex align-center mb-2">
            <v-icon color="secondary" class="mr-2"
              >mdi-lightbulb-on-outline</v-icon
            >
            <span class="font-weight-bold"
              >Why this recommendation was made</span
            >
          </div>
          <p v-if="narrative" class="banner-narrative mt-4 mb-0">
            {{ narrative }}
          </p>
        </v-card>

        <!-- Per-garage detail cards -->
        <div class="garage-grid mb-4">
          <v-card
            v-for="(garage, index) in garages"
            :key="garage.garageId"
            outlined
            elevation="0"
            class="rounded-lg pa-4 garage-card"
            :class="{ 'is-top': index === 0 }"
          >
            <div class="d-flex align-center mb-3">
              <v-avatar
                :color="index === 0 ? 'secondary' : 'grey lighten-2'"
                size="34"
                class="mr-3"
              >
                <span
                  class="font-weight-bold"
                  :class="
                    index === 0 ? 'white--text' : 'grey--text text--darken-2'
                  "
                  >{{ garage.garageId }}</span
                >
              </v-avatar>
              <span class="text-subtitle-1 font-weight-bold tw-min-w-0">{{
                garage.garageName
              }}</span>
              <v-spacer />
              <span
                class="fit-badge"
                :class="index === 0 ? 'fit-good' : 'fit-low'"
              >
                {{
                  index === 0 ? 'Best overall fit' : 'Lower historical match'
                }}
              </span>
            </div>

            <div class="card-cols">
              <div>
                <div class="kv">
                  <v-icon small class="mr-2">mdi-map-marker</v-icon>
                  <span class="k">Region</span>
                  <span class="v">{{ garage.region || 'N/A' }}</span>
                </div>
                <div class="kv">
                  <v-icon small class="mr-2">mdi-shield-check-outline</v-icon>
                  <span class="k">Rare &amp; Specialized</span>
                  <span class="v">{{
                    garage.rareAndSpecialized ? 'Yes' : 'No'
                  }}</span>
                </div>
                <div class="kv">
                  <v-icon small class="mr-2" color="secondary"
                    >mdi-trophy-outline</v-icon
                  >
                  <span class="k">Score</span>
                  <span class="v">{{ garage.score }}</span>
                </div>
              </div>
              <div>
                <div class="kv">
                  <v-icon small class="mr-2" color="success"
                    >mdi-check-circle-outline</v-icon
                  >
                  <span class="k">Exact Match</span>
                  <span class="v">{{ garage.exactMatchCount }}</span>
                </div>
                <div class="kv">
                  <v-icon small class="mr-2" color="primary"
                    >mdi-car-multiple</v-icon
                  >
                  <span class="k">Model Match</span>
                  <span class="v">{{ garage.modelMatchCount }}</span>
                </div>
                <div class="kv">
                  <v-icon small class="mr-2" color="secondary"
                    >mdi-wrench</v-icon
                  >
                  <span class="k">Make Match</span>
                  <span class="v">{{ garage.makeMatchCount }}</span>
                </div>
                <div class="kv">
                  <v-icon small class="mr-2">mdi-calendar-check</v-icon>
                  <span class="k">Last Repaired</span>
                  <span class="v">{{
                    garage.lastRepairedAt | formatToHuman
                  }}</span>
                </div>
              </div>
            </div>
          </v-card>
        </div>

        <!-- Comparison table + rationale -->
        <div class="bottom-grid">
          <v-card outlined elevation="0" class="rounded-lg overflow-hidden">
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr class="cmp-head">
                    <th scope="col" class="text-left">Comparison</th>
                    <th
                      v-for="garage in garages"
                      :key="garage.garageId"
                      scope="col"
                      class="text-center"
                    >
                      {{ garage.garageName }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Repaired same make, model and YOM</td>
                    <td
                      v-for="garage in garages"
                      :key="garage.garageId"
                      class="text-center"
                    >
                      {{ garage.exactMatchCount }}
                    </td>
                  </tr>
                  <tr>
                    <td>Repaired same make and model</td>
                    <td
                      v-for="garage in garages"
                      :key="garage.garageId"
                      class="text-center"
                    >
                      {{ garage.modelMatchCount }}
                    </td>
                  </tr>
                  <tr>
                    <td>Repaired same make</td>
                    <td
                      v-for="garage in garages"
                      :key="garage.garageId"
                      class="text-center"
                    >
                      {{ garage.makeMatchCount }}
                    </td>
                  </tr>
                  <tr class="cmp-total">
                    <td class="font-weight-bold">Score</td>
                    <td
                      v-for="garage in garages"
                      :key="garage.garageId"
                      class="text-center font-weight-bold"
                    >
                      {{ garage.score }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </div>
      </template>

      <!-- Empty state -->
      <div
        v-else
        class="text-body-2 grey--text text--darken-1 py-4 text-center"
      >
        No recommendation available yet.
      </div>
    </div>
  </v-card>
</template>

<script>
import { formatToHuman } from '@/utils/time'

export default {
  name: 'GarageRecommendation',
  emits: ['regenerate'],
  props: {
    // The `data` object from GET /api/recommendations/{bookingId}
    recommendation: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    generating: { type: Boolean, default: false },
    // When true, shows the "regenerate" button (emits `regenerate`).
    allowRegenerate: { type: Boolean, default: false },
    regenerating: { type: Boolean, default: false },
  },
  computed: {
    garages() {
      return (
        (this.recommendation && this.recommendation.recommendedGarages) || []
      )
    },
    topPick() {
      return this.garages[0] || null
    },
    narrative() {
      return (this.recommendation && this.recommendation.insightNarrative) || ''
    },
    vehicleLabel() {
      const r = this.recommendation
      if (!r) return ''
      return [r.yearOfManufacture, r.make, r.model].filter(Boolean).join(' ')
    },
    show() {
      return this.loading || this.generating || this.garages.length > 0
    },
  },
  filters: {
    formatToHuman,
  },
}
</script>

<style scoped>
.garage-recommendation {
  border-color: #e3e8ef;
}
.meta-strip {
  border-color: #e3e8ef;
  background: #fbfcfe;
}
.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px 24px;
}
.meta-item {
  display: flex;
  align-items: center;
  min-width: 0;
}
.meta-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-weight: 600;
  color: #3f4a5a;
}
.meta-value {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  overflow-wrap: break-word;
}

/* Recommended banner */
.recommend-banner {
  background: linear-gradient(135deg, #fff8ec 0%, #fdeecb 100%);
  border: 1px solid #f3d489;
}
.banner-medal {
  background: linear-gradient(135deg, #f8ab10 0%, #e08e00 100%);
}
.banner-title {
  color: #8a5a00;
  line-height: 1.2;
}
.banner-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px 20px;
}
.bstat {
  display: flex;
  align-items: center;
  font-size: 13px;
}
.bstat-label {
  color: #9a7a3a;
  margin-right: 6px;
}
.bstat-value {
  font-weight: 700;
  color: #5c4a20;
}
.banner-narrative {
  font-size: 14px;
  line-height: 1.6;
  color: #5c4a20;
}

/* Garage detail cards */
.garage-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}
.garage-card {
  border-color: #e8ebf0;
}
.garage-card.is-top {
  border: 1px solid #f8ab10;
  box-shadow: 0 8px 24px rgba(3, 78, 162, 0.12);
}
.card-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 20px;
}
.kv {
  display: flex;
  align-items: center;
  padding: 5px 0;
  font-size: 13px;
  border-bottom: 1px solid #f4f6f9;
}
.kv .k {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-weight: 600;
  color: #3f4a5a;
}
.kv .v {
  margin-left: auto;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.fit-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  white-space: nowrap;
}
.fit-good {
  background: #e6f4ea;
  color: #1e7e34;
}
.fit-low {
  background: #fdecea;
  color: #c0392b;
}

/* Bottom grid: comparison + rationale */
.bottom-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}
.cmp-head th {
  background: linear-gradient(90deg, #034ea2 0%, #0a63c4 100%);
  color: #ffffff !important;
  font-weight: 600;
}
.cmp-total td {
  background: #f5f8fd;
}
.why-card {
  background: #fffaf0;
  border: 1px solid #f3d489;
}

@media (min-width: 960px) {
  .garage-grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
